"use client";

import { Spinner } from "@decibeltrade/web-core/components";
import { useTranslations } from "next-intl";
import { css } from "styled-system/css";
import { center, stack } from "styled-system/patterns";

import { Breadcrumb } from "@/components/breadcrumb";
import { FftDashboard } from "@/components/fft/fft-dashboard";
import { usePrimarySubaccountAvailableRaw } from "@/components/fft/lock-funding";
import { HandleSuspenseQuery } from "@/components/handle-query";
import { useActiveTrial } from "@/contexts/queries/funded-trial/active-trial";
import { useFundedTrialEligibility } from "@/contexts/queries/funded-trial/eligibility";
import { useUsdcBalance } from "@/contexts/queries/usdc-balance";
import { useDecibelWallet } from "@/hooks/use-decibel-wallet";
import { amountToChainUnits } from "@/utils/math";

import {
  buildSyntheticEligibility,
  FftDashboardOverride,
  useFftDashboardOverride,
} from "./test-utils/state-override";

export function FftDashboardContent() {
  const t = useTranslations("firstTradeOnUs");
  const { accountAddress } = useDecibelWallet();
  const override = useFftDashboardOverride();

  return (
    <PageShell breadcrumbHubLabel={t("breadcrumbHub")} pageTitle={t("pageTitle")}>
      {override ? (
        <FftDashboardOverridden override={override} />
      ) : !accountAddress ? (
        <div
          className={center({ p: "48", color: "text.tertiary" })}
          data-testid="fft-dashboard-no-wallet"
          data-step-state="connect"
        >
          {t("connectPrompt")}
        </div>
      ) : (
        <HandleSuspenseQuery
          loading={
            <div className={center({ p: "48" })}>
              <Spinner size="lg" />
            </div>
          }
          error={({ error }) => (
            <div
              className={center({ p: "48", color: "text.negative" })}
              data-testid="fft-dashboard-error"
            >
              {error instanceof Error ? error.message : t("loadError")}
            </div>
          )}
        >
          <FftDashboardLoaded accountAddress={accountAddress} />
        </HandleSuspenseQuery>
      )}
    </PageShell>
  );
}

interface PageShellProps {
  breadcrumbHubLabel: string;
  pageTitle: string;
  children: React.ReactNode;
}

function PageShell({ breadcrumbHubLabel, pageTitle, children }: PageShellProps) {
  return (
    <main className={mainStyles} data-testid="fft-dashboard-page">
      <Breadcrumb
        items={[
          { label: breadcrumbHubLabel, href: "/rewards" },
          { label: pageTitle, isCurrentPage: true },
        ]}
      />
      <header className={stack({ gap: "4" })}>
        <h1 className={pageTitleStyles}>{pageTitle}</h1>
      </header>
      {children}
    </main>
  );
}

function FftDashboardOverridden({ override }: { override: FftDashboardOverride }) {
  const eligibility = buildSyntheticEligibility(override.eligibility);
  const activeTrial = override.activeTrial ?? null;
  const usdcBalanceRaw = BigInt(amountToChainUnits(override.usdcBalance ?? 1000));
  const subaccountAvailableRaw = BigInt(amountToChainUnits(override.subaccountAvailable ?? 0));

  return (
    <FftDashboard
      eligibility={eligibility}
      activeTrial={activeTrial}
      usdcBalanceRaw={usdcBalanceRaw}
      subaccountAvailableRaw={subaccountAvailableRaw}
      stepOverride={override.step}
      trackingDisabled
    />
  );
}

interface FftDashboardLoadedProps {
  accountAddress: string;
}

function FftDashboardLoaded({ accountAddress }: FftDashboardLoadedProps) {
  // Single Suspense boundary above us — every read here throws to it.
  const { data: eligibility } = useFundedTrialEligibility({ account: accountAddress });
  const { data: activeTrial } = useActiveTrial({ account: accountAddress });
  const { data: usdcBalanceDisplay } = useUsdcBalance({ accountAddr: accountAddress });
  const subaccountAvailableRaw = usePrimarySubaccountAvailableRaw();

  const usdcBalanceRaw = BigInt(amountToChainUnits(usdcBalanceDisplay));

  return (
    <FftDashboard
      eligibility={eligibility}
      activeTrial={activeTrial}
      usdcBalanceRaw={usdcBalanceRaw}
      subaccountAvailableRaw={subaccountAvailableRaw}
      account={accountAddress}
    />
  );
}

const mainStyles = stack({
  gap: "20",
  w: "full",
  flex: "1",
  px: { base: "16", md: "20" },
  py: { base: "20", md: "32" },
});

const pageTitleStyles = css({ textStyle: "subheading.md", color: "text.primary" });
